import { DEFAULT_CURRENCY } from "@configs/currencies";

interface LadderSource<T> {
  pricingLadder?: T[] | null;
  pricingLadderCurrencies?: unknown;
}

/**
 * The volume ladder a photo size charges in `currency`, or null when there is
 * none — mirroring the backend's PricingService.getPricingLadder, which is what
 * the customer is actually charged.
 *
 * eur gets the base `pricingLadder`. Every other currency, eur-at included,
 * gets its own entry in `pricingLadderCurrencies` or no ladder at all: never
 * the euro one. Falling back to it showed the RON drawer "0,24 Lei / photo, at
 * 150 photos 0,20 Lei" — the EUR ladder's cents labelled as Lei — against a
 * real flat 0,79 Lei, and promised a discount that did not exist (currency
 * walk B4).
 *
 * The lookup is case-insensitive like the backend's ladderInCurrency: the data
 * carries both "pln" and "PLN".
 */
export function ladderForCurrency<T>(
  photoSize: LadderSource<T> | null | undefined,
  currency: string | null | undefined,
): T[] | null {
  if (!photoSize || !currency) return null;
  const wanted = currency.toLowerCase();
  if (wanted === DEFAULT_CURRENCY) {
    return photoSize.pricingLadder?.length ? photoSize.pricingLadder : null;
  }
  const byCurrency = photoSize.pricingLadderCurrencies;
  if (!byCurrency || typeof byCurrency !== "object") return null;
  for (const [key, ladder] of Object.entries(byCurrency)) {
    if (key.toLowerCase() === wanted && Array.isArray(ladder) && ladder.length) {
      return ladder as T[];
    }
  }
  return null;
}
